import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ErrorBanner from '../../common/ErrorBanner';
import {
  getGlobalVoiceNumbers,
  createVoiceTestCall,
  playVoiceMessage,
  getVoiceCalls,
} from '../../../api/backendApi';

const VoiceTestConsoleTab = () => {
  const [fromOptions, setFromOptions] = useState([]);
  const [loadingFroms, setLoadingFroms] = useState(true);
  const [error, setError] = useState(null);

  const [toNumber, setToNumber] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [message, setMessage] = useState('');
  const [testType, setTestType] = useState('play'); // 'play' | 'dial'

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { sid, status }

  const [pollSid, setPollSid] = useState(null);
  const [callInfo, setCallInfo] = useState(null);
  const pollTimerRef = useRef(null);

  const canSubmit = useMemo(() => !!toNumber && !!fromNumber && !submitting, [toNumber, fromNumber, submitting]);

  const loadFromNumbers = useCallback(async () => {
    try {
      setError(null);
      setLoadingFroms(true);
      const resp = await getGlobalVoiceNumbers();
      // Normalize array shape
      const items = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      const opts = items
        .map((x) => ({ value: x?.e164 || x?.number || '', label: x?.e164 || x?.number || '', disabled: x?.status === 'disabled' }))
        .filter((o) => o.value);
      setFromOptions(opts);
      if (!fromNumber && opts.length > 0) setFromNumber(opts[0].value);
    } catch (e) {
      setError(e?.message || 'Failed to load voice numbers');
    } finally {
      setLoadingFroms(false);
    }
  }, [fromNumber]);

  useEffect(() => {
    loadFromNumbers();
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [loadFromNumbers]);

  const startPollingCall = useCallback((sid) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    setPollSid(sid);
    // immediate check, then every 5s
    const check = async () => {
      try {
        const list = await getVoiceCalls();
        const calls = Array.isArray(list?.data) ? list.data : (Array.isArray(list) ? list : []);
        const match = calls.find((c) => c?.sid === sid);
        if (match) {
          setCallInfo(match);
          const st = String(match.status || '').toLowerCase();
          if (!['in-progress', 'queued', 'ringing', 'busy'].includes(st)) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
        } else {
          // no longer present -> assume ended
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      } catch (_) {
        // ignore poll errors
      }
    };
    check();
    pollTimerRef.current = setInterval(check, 5000);
  }, []);

  const onSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError(null);
      setResult(null);
      setCallInfo(null);
      setPollSid(null);

      if (testType === 'play') {
        const r = await createVoiceTestCall({ to: toNumber, from: fromNumber, message, type: 'play' });
        setResult(r);
        if (r?.sid) startPollingCall(r.sid);
      } else {
        // Dial SIP or number
        const r = await createVoiceTestCall({ to: toNumber, from: fromNumber, message, type: 'dial' });
        setResult(r);
        if (r?.sid) startPollingCall(r.sid);
      }
    } catch (e) {
      setError(e?.message || 'Failed to initiate test call');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, testType, toNumber, fromNumber, message, startPollingCall]);

  return (
    <Box>
      <ErrorBanner message={error} />

      <Card variant="outlined" sx={{ p: 2, mt: 2, maxWidth: 600 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Voice Test Console</Typography>
          <form onSubmit={onSubmit}>
            <Stack spacing={2}>
              <TextField
                label="To Number or SIP URI"
                placeholder="e.g. +14155551212 or sip:alice@example.com"
                value={toNumber}
                onChange={(e) => setToNumber(e.target.value)}
                required
                fullWidth
                size="small"
              />

              <FormControl fullWidth size="small">
                <InputLabel id="from-number-label">From Number</InputLabel>
                <Select
                  labelId="from-number-label"
                  label="From Number"
                  value={fromNumber}
                  onChange={(e) => setFromNumber(e.target.value)}
                  disabled={loadingFroms}
                >
                  {fromOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Message Text"
                placeholder="What should be spoken on the call (Play Message)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                size="small"
              />

              <FormControl>
                <FormLabel>Test Type</FormLabel>
                <RadioGroup
                  row
                  value={testType}
                  onChange={(e) => setTestType(e.target.value)}
                >
                  <FormControlLabel value="play" control={<Radio />} label="Play Message" />
                  <FormControlLabel value="dial" control={<Radio />} label="Dial SIP" />
                </RadioGroup>
              </FormControl>

              <Box>
                <Button type="submit" variant="contained" disabled={!canSubmit} startIcon={submitting ? <CircularProgress size={16} /> : null}>
                  Make Test Call
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Result</Typography>
          {result?.sid && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Call initiated. SID: {result.sid}
            </Alert>
          )}
          {!result && (
            <Typography variant="body2" color="text.secondary">No call yet.</Typography>
          )}

          {pollSid && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Live Call Status</Typography>
              {callInfo ? (
                <Stack spacing={1}>
                  <Typography variant="body2">Status: {callInfo.status || '-'}</Typography>
                  <Typography variant="body2">From: {callInfo.from || '-'}</Typography>
                  <Typography variant="body2">To: {callInfo.to || '-'}</Typography>
                  <Typography variant="body2">Duration: {callInfo.duration ?? 0}s</Typography>
                  <Typography variant="body2">Direction: {callInfo.direction || '-'}</Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Waiting for call info...</Typography>
                </Stack>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VoiceTestConsoleTab;
